export type StudioLoadMilestone = "complete" | "first_usable";

export interface StudioPageReady {
  datasetId: string;
  cacheHit: boolean;
  eventCount: number;
}

/** One successful ready-view measurement per dataset route visit. */
export class StudioPageLoadTracker {
  private firstNavigation = true;
  private pathname: string | null = null;
  private visit: {
    datasetId: string;
    startedAt: number;
    timingOrigin: "navigation_start" | "route_change";
    reported: Set<string>;
    wasBackgrounded: boolean;
  } | null = null;

  enter(pathname: string, now: number, documentPath: string | null, hidden: boolean) {
    // React StrictMode replay and query/filter edits are the same visit.
    if (this.pathname === pathname) return;
    const fromDocumentStart = this.firstNavigation && documentPath === pathname;
    this.firstNavigation = false;
    this.pathname = pathname;
    const match = /^\/(?:live-demo\/)?dataset-studio\/([^/]+)\/?$/.exec(pathname);
    this.visit = match ? {
      datasetId: decodeURIComponent(match[1]),
      startedAt: fromDocumentStart ? 0 : now,
      timingOrigin: fromDocumentStart ? "navigation_start" : "route_change",
      reported: new Set(),
      wasBackgrounded: hidden,
    } : null;
  }

  backgrounded() {
    if (this.visit) this.visit.wasBackgrounded = true;
  }

  ready(result: StudioPageReady, now: number, milestone: StudioLoadMilestone = "complete") {
    const visit = this.visit;
    if (!visit || visit.reported.has(milestone) || visit.datasetId !== result.datasetId) return null;
    visit.reported.add(milestone);
    return {
      dataset_id: result.datasetId,
      readiness: milestone,
      duration_ms: Math.round(Math.max(0, now - visit.startedAt)),
      cache_hit: result.cacheHit,
      cache_source: result.cacheHit ? "published_snapshot" : "live",
      event_count: result.eventCount,
      timing_origin: visit.timingOrigin,
      was_backgrounded: visit.wasBackgrounded,
    };
  }
}
